import styled from "@emotion/styled";
import { Doughnut } from "react-chartjs-2";
import { ThemeProps } from "types/theme";
import { withTheme } from "@emotion/react";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LayoutPosition,
  Title,
} from "chart.js";
import { useTranslation } from "react-i18next";
import { Text } from "components/atoms/text";
import { Grid, Theme, Typography, useTheme } from "@mui/material";
import { grey } from "@mui/material/colors";
import { makeStyles } from "@mui/styles";
import { abbreviateNumber } from "core/helpers/abbreviate-number";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const min = -10;
const max = 10;

const useStyles = makeStyles<Theme, { value: number }>((theme: Theme) => ({
  track: {
    position: "relative",
    width: "100%",
    height: "4px",
    backgroundImage: `linear-gradient(to right, ${theme.palette.error.main}, ${theme.palette.success.dark})`,
    borderRadius: "2px",
    marginTop: "15px",
    marginBottom: "20px",
    padding: "0 16px",
  },
  thumb: {
    position: "absolute",
    height: "15px",
    width: "15px",
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    top: "-6px",
  },
  valueLabel: {
    position: "absolute",
    top: "10px",
    transform: "translateX(-50%)",
    fontSize: "11px",
    color: theme.palette.text.secondary,
  },
  startLabel: {
    position: "absolute",
    color: theme.palette.text.secondary,
    fontSize: "11px",
    left: 0,
    top: "5px",
  },
  edgeLabel: {
    position: "absolute",
    color: theme.palette.text.secondary,
    fontSize: "11px",
    right: 0,
    top: "5px",
  },
}));

interface CustomSliderProps {
  value: number;
}

function CustomSlider({ value }: CustomSliderProps) {
  const theme = useTheme();
  const classes = useStyles({ value: (value / 10) * 100 });
  const { t } = useTranslation();
  const valuePosition = ((value - min) / (max - min)) * 100;

  return (
    <>
      <Typography
        variant="body1"
        fontSize={"12px"}
        fontWeight={"700"}
        color="#616161"
        mt={"8px"}
      >
        {t("social-reports.posts.slider-title")}
      </Typography>
      <div className={classes.track}>
        <div className={classes.startLabel}>-10</div>
        <div
          className={classes.thumb}
          style={{
            left: `${valuePosition}%`,
            backgroundColor:
              valuePosition <= 33
                ? theme.palette.error.main
                : valuePosition <= 66
                ? grey[400]
                : theme.palette.success.dark,
          }}
        />
        <div
          className={classes.valueLabel}
          style={{ left: `${valuePosition + 2}%` }}
        >
          {value.toFixed(2)}
        </div>
        <div className={classes.edgeLabel}>10</div>
      </div>
    </>
  );
}

const ChartWrapper = styled.div`
  height: 160px;
  position: relative;
`;

const DoughnutInner = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 0;
  margin-top: -22px;
  text-align: center;
  z-index: 0;
`;

type Props = {
  valuesCount: { [key: string]: number };
  commentsCount: number;
  sentimentScale: number;
};

const PostAnalysis = ({
  theme,
  valuesCount,
  commentsCount,
  sentimentScale,
}: ThemeProps & Props) => {
  const { t } = useTranslation();

  const data = {
    labels: Object.keys(valuesCount).map((key) =>
      t(`social-reports.posts.${key}`)
    ),
    datasets: [
      {
        data: Object.values(valuesCount).map((g) => g),
        backgroundColor: [
          theme.palette.error.main,
          theme.palette.success.dark,
          grey[400],
        ],
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: "bottom" as LayoutPosition,
        labels: {
          boxWidth: 15,
          boxHeight: 15,
          // generateLabels: (chart: Chart) => {
          //   const data: ChartData = chart.data;
          //   if (data.datasets.length) {
          //     return data.labels?.map((label, i) => {
          //       const dataset = data.datasets[0];
          //       const value = data.datasets[0].data[i];

          //       const backgroundColor = Array.isArray(dataset.backgroundColor)
          //         ? dataset.backgroundColor[i]
          //         : dataset.backgroundColor;

          //       return {
          //         text: `${label} (${value})`,
          //         fillStyle: backgroundColor,
          //         strokeStyle: "#fff",
          //         lineWidth: data.datasets[0].borderWidth as number,
          //         hidden: !chart.getDataVisibility(i),
          //         index: i,
          //         borderRadius: 5,
          //       };
          //     }) as LegendItem[];
          //   }
          //   return [];
          // },
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    elements: {
      arc: {
        borderWidth: 2,
      },
    },
    cutout: "80%",
  };

  const calculatePositive = () => {
    const total =
      valuesCount.positive_count +
      valuesCount.negative_count +
      valuesCount.neutral_count;

    if (total === 0) return "0%";

    const positivePercentage = (valuesCount.positive_count / total) * 100;

    return positivePercentage.toFixed(1) + "%";
  };

  const calculateNeutral = () => {
    const total =
      valuesCount.positive_count +
      valuesCount.negative_count +
      valuesCount.neutral_count;

    if (total === 0) return "0%";

    const neutralPercentage = (valuesCount.neutral_count / total) * 100;

    return neutralPercentage.toFixed(1) + "%";
  };

  const calculateNegative = () => {
    const total =
      valuesCount.positive_count +
      valuesCount.negative_count +
      valuesCount.neutral_count;

    if (total === 0) return "0%";

    const negativePercentage = (valuesCount.negative_count / total) * 100;

    return negativePercentage.toFixed(1) + "%";
  };

  return (
    <>
      <Text
        padding={"10px 0px"}
        fontWeight={700}
        style={{
          color: grey[700],
        }}
        fontSize={"12px"}
      >
        {t("social-reports.posts.analyzed") + " " + commentsCount}
      </Text>
      <Grid container spacing={2} display={"flex"} alignItems={"center"}>
        <Grid item xs={12} sm={6}>
          {valuesCount.positive_count +
            valuesCount.negative_count +
            valuesCount.neutral_count ===
            0 && <Typography>{t("empty_states.empty_stats.title")}</Typography>}
          <ChartWrapper>
            <DoughnutInner>
              <Text fontWeight={"700"} fontSize={"20px"}>
                {calculatePositive()}
              </Text>
              <Text variant="caption" fontWeight={"400"} fontSize={"11px"}>
                {t("social-reports.posts.positive_count")}
              </Text>
            </DoughnutInner>
            <Doughnut data={data} options={options} />
          </ChartWrapper>
        </Grid>
        <Grid item container xs={12} sm={6} spacing={7} display={"flex"}>
          <Grid item>
            <Text
              fontSize={"9px"}
              fontWeight={"600"}
              sx={{
                color: theme.palette.grey[600],
              }}
            >
              {t("social-reports.posts.positive_count")}
            </Text>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
              }}
            >
              <div
                style={{
                  backgroundColor: theme.palette.success.dark,
                  width: "10px",
                  height: "10px",
                  borderRadius: "15px",
                  marginRight: "5px",
                }}
              ></div>
              <Text fontSize={"14px"} fontWeight={"700"}>
                {calculatePositive()}
              </Text>
              <Text fontSize={"9px"} fontWeight={"400"} ml={"5px"}>
                {abbreviateNumber(valuesCount.positive_count)}
              </Text>
            </div>
          </Grid>
          <Grid item>
            <Text
              fontSize={"9px"}
              fontWeight={"600"}
              sx={{
                color: theme.palette.grey[600],
              }}
            >
              {t("social-reports.posts.negative_count")}
            </Text>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
              }}
            >
              <div
                style={{
                  backgroundColor: theme.palette.error.main,
                  width: "10px",
                  height: "10px",
                  borderRadius: "15px",
                  marginRight: "5px",
                }}
              ></div>
              <Text fontSize={"14px"} fontWeight={"700"}>
                {calculateNegative()}
              </Text>
              <Text fontSize={"9px"} fontWeight={"400"} ml={"5px"}>
                {abbreviateNumber(valuesCount.negative_count)}
              </Text>
            </div>
          </Grid>
          <Grid item>
            <Text
              fontSize={"9px"}
              fontWeight={"600"}
              sx={{
                color: theme.palette.grey[600],
              }}
            >
              {t("social-reports.posts.neutral_count")}
            </Text>
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
              }}
            >
              <div
                style={{
                  backgroundColor: grey[400],
                  width: "10px",
                  height: "10px",
                  borderRadius: "15px",
                  marginRight: "5px",
                }}
              ></div>
              <Text fontSize={"14px"} fontWeight={"700"}>
                {calculateNeutral()}
              </Text>
              <Text fontSize={"9px"} fontWeight={"400"} ml={"5px"}>
                {abbreviateNumber(valuesCount.neutral_count)}
              </Text>
            </div>
          </Grid>
        </Grid>
      </Grid>
      <CustomSlider value={sentimentScale} />
    </>
  );
};

export default withTheme(PostAnalysis);
